import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { JobTitleVerdict } from "@signalist/shared-types";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { SearchField } from "@/components/SearchField/SearchField";
import { Spinner } from "@/components/Spinner/Spinner";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import {
	useAddJobTitlesMutation,
	useDeleteJobTitleMutation,
	useGetJobTitlesQuery,
	useMoveJobTitlesMutation,
	usePrefillJobTitlesMutation,
} from "@/pages/Private/redux/jobTitleScreening/jobTitleScreening.api";
import {
	clearSelection,
	excludedTitlesSelector,
	jobTitleCountsSelector,
	jobTitleSearchSelector,
	jobTitleSelectionSelector,
	setSearch,
	toggleSelect,
	whitelistedTitlesSelector,
} from "@/pages/Private/redux/jobTitleScreening/jobTitleScreening.slice";

import { ScoreShell } from "../../shell/ScoreShell";
import { JobTitleColumn } from "./JobTitleColumn";
import { JobTitleSelectionToast } from "./JobTitleSelectionToast";

const oppositeVerdict = (verdict: JobTitleVerdict): JobTitleVerdict =>
	verdict === JobTitleVerdict.WHITELISTED ? JobTitleVerdict.EXCLUDED : JobTitleVerdict.WHITELISTED;

export const ScoreJobTitles: FunctionComponent = () => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();

	const search = useAppSelector(jobTitleSearchSelector);
	const whitelisted = useAppSelector(whitelistedTitlesSelector);
	const excluded = useAppSelector(excludedTitlesSelector);
	const { whitelistCount, excludedCount } = useAppSelector(jobTitleCountsSelector);
	const { selectedIds, selectionColumn } = useAppSelector(jobTitleSelectionSelector);

	// Debounced copy drives the server-side search for large sets; the columns already filter
	// the loaded page client-side via the selectors, so typing stays responsive.
	const [debouncedSearch, setDebouncedSearch] = useState("");

	useEffect(() => {
		const id = setTimeout(() => setDebouncedSearch(search), 300);

		return () => clearTimeout(id);
	}, [search]);

	const { isLoading } = useGetJobTitlesQuery({ searchValue: debouncedSearch || undefined });

	const [addJobTitles] = useAddJobTitlesMutation();
	const [moveJobTitles] = useMoveJobTitlesMutation();
	const [deleteJobTitle] = useDeleteJobTitleMutation();
	const [prefillJobTitles, { isLoading: isPrefilling }] = usePrefillJobTitlesMutation();

	const handleAdd = (title: string, verdict: JobTitleVerdict) => {
		addJobTitles({ titles: [title], verdict });
	};

	const handleMoveOne = (id: number, from: JobTitleVerdict) => {
		moveJobTitles({ ids: [id], toVerdict: oppositeVerdict(from) });
	};

	const handleMoveSelected = () => {
		if (!selectionColumn || !selectedIds.length) {
			return;
		}

		moveJobTitles({ ids: selectedIds, toVerdict: oppositeVerdict(selectionColumn) });
		dispatch(clearSelection());
	};

	const isEmpty = !isLoading && whitelistCount === 0 && excludedCount === 0 && !search;

	return (
		<ScoreShell
			description={t("leadScoring.jobTitles.subtitle")}
			intro={t("leadScoring.stepper.job-titles")}
			step="job-titles"
		>
			<div className="mb-4 flex flex-wrap items-center justify-between gap-3">
				<div className="w-full max-w-xs">
					<SearchField
						handleChange={(event) => dispatch(setSearch(event.target.value))}
						handleReset={() => dispatch(setSearch(""))}
						name="job-title-search"
						placeholder={t("leadScoring.jobTitles.searchPlaceholder")}
						testId="job-title-search"
						value={search}
					/>
				</div>
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					isLoading={isPrefilling}
					size={ButtonSize.XS}
					testId="job-title-prefill"
					title={t("leadScoring.jobTitles.prefill")}
					type="button"
					onClick={() => prefillJobTitles()}
				/>
			</div>

			{isLoading ? (
				<div className="flex h-64 items-center justify-center" data-test="job-title-loading">
					<Spinner />
				</div>
			) : isEmpty ? (
				<div
					className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-gray-200 px-6 py-16 text-center"
					data-test="job-title-empty"
				>
					<span className="text-base font-semibold text-text">
						{t("leadScoring.jobTitles.emptyTitle")}
					</span>
					<span className="max-w-md text-sm text-text-meta">
						{t("leadScoring.jobTitles.emptyBody")}
					</span>
					<Button
						color={ButtonColor.BRAND}
						isLoading={isPrefilling}
						size={ButtonSize.S}
						testId="job-title-empty-prefill"
						title={t("leadScoring.jobTitles.emptyPrefill")}
						type="button"
						onClick={() => prefillJobTitles()}
					/>
				</div>
			) : (
				<div className="grid max-h-[64vh] min-h-[420px] grid-cols-1 gap-4 tb:grid-cols-2">
					<JobTitleColumn
						count={whitelistCount}
						isLocked={selectionColumn === JobTitleVerdict.EXCLUDED}
						rows={whitelisted}
						selectedIds={selectedIds}
						verdict={JobTitleVerdict.WHITELISTED}
						onAdd={handleAdd}
						onDelete={deleteJobTitle}
						onMoveOne={handleMoveOne}
						onToggleSelect={(id, column) => dispatch(toggleSelect({ id, column }))}
					/>
					<JobTitleColumn
						count={excludedCount}
						isLocked={selectionColumn === JobTitleVerdict.WHITELISTED}
						rows={excluded}
						selectedIds={selectedIds}
						verdict={JobTitleVerdict.EXCLUDED}
						onAdd={handleAdd}
						onDelete={deleteJobTitle}
						onMoveOne={handleMoveOne}
						onToggleSelect={(id, column) => dispatch(toggleSelect({ id, column }))}
					/>
				</div>
			)}

			{selectionColumn && (
				<JobTitleSelectionToast
					count={selectedIds.length}
					selectionColumn={selectionColumn}
					onClear={() => dispatch(clearSelection())}
					onMove={handleMoveSelected}
				/>
			)}
		</ScoreShell>
	);
};
